import React from 'react';

/** Coloured state chip. Three states, because "configured but not connected" is not a failure. */
export function StatusPill({ tone, children }) {
  const tones = {
    good: 'bg-green-50 border-green-200 text-green-700',
    pending: 'bg-amber-50 border-amber-200 text-amber-700',
    bad: 'bg-red-50 border-red-200 text-red-600',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] font-semibold ${tones[tone]}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

/** One deployment prerequisite, and what to do when it is missing. */
export function CheckRow({ ok, label, value, missingHint }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span
        className={`material-symbols-outlined text-[18px] mt-0.5 ${ok ? 'text-green-600' : 'text-red-500'}`}
      >
        {ok ? 'check_circle' : 'cancel'}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-[#0f172a]">{label}</p>
        {ok ? (
          value && <p className="text-[12px] text-[#64748b] font-mono break-all mt-0.5">{value}</p>
        ) : (
          <p className="text-[12px] text-red-600 mt-0.5">{missingHint}</p>
        )}
      </div>
    </div>
  );
}

/** The card shell, so both cards get the same header shape without repeating it. */
export function IntegrationCard({ icon, name, role, pill, children }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-[10px] bg-[#f1f5f9] flex items-center justify-center">
            <span className="material-symbols-outlined text-[20px] text-[#334155]">{icon}</span>
          </div>
          <div>
            <p className="text-[16px] font-semibold text-[#0f172a] leading-tight">{name}</p>
            <p className="text-[13px] text-[#64748b] mt-0.5">{role}</p>
          </div>
        </div>
        {pill}
      </div>
      {children}
    </div>
  );
}

/**
 * The four Microsoft variables, with the mistake each one actually attracts.
 *
 * The secret is worth its hint: Entra shows Secret ID and Value in adjacent columns and only the
 * Value is the secret. Copying the wrong one produces a deployment that reports configured: true
 * and fails on the first send, which is exactly the class of error this page exists to shorten.
 */
export const GRAPH_VARS = [
  {
    name: 'GRAPH_TENANT_ID',
    missingHint: 'Not set in Render. The Directory (tenant) ID on the Entra app registration.',
  },
  {
    name: 'GRAPH_CLIENT_ID',
    missingHint: 'Not set in Render. The Application (client) ID on the Entra app registration.',
  },
  {
    name: 'GRAPH_CLIENT_SECRET',
    missingHint:
      'Not set in Render. Copy the secret Value, not the Secret ID - Entra shows them in adjacent columns and only the Value works.',
  },
  {
    name: 'GRAPH_SENDER',
    missingHint: 'Not set in Render. The mailbox to send as, e.g. support@medeffectsllc.com.',
  },
];
